import {
  AgendaSlotReservationApi,
  BookAgendaSlotApiPayload,
  BookAgendaSlotOccurenceApiPayload,
  HttpErrors,
  SlotReservationDoctorSubstitute,
  workScheduleBetweenItems,
} from '@omecare/utils';
import { UseMutationResult, useMutation } from '@tanstack/react-query';
import { setHours } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';
import { getErrorPayload } from '@/utils/errors/get-error-data';
import { useSlotBookingConflitErrorHandler } from '@/utils/errors/use-slot-booking-conflit.error-handler';
import http from '@/utils/http.service';
import { agendaSlotReservationSchema } from '../queries/use-get-office-agendas-slots';

const bookAgendaSlotResponseSchema = z.object({
  id: z.string().uuid(),
  reservations: z.array(agendaSlotReservationSchema),
});

export type BookAgendaSlotResponse = z.infer<
  typeof bookAgendaSlotResponseSchema
>;
export type BookAgendaSlotPayload = {
  id: string;
  occurrence?: Omit<BookAgendaSlotOccurenceApiPayload, 'endAt'> & {
    endAt: Date | null;
  };
} & Omit<BookAgendaSlotApiPayload, 'doctorSubstituteId' | 'occurrence'>;

export const transformApiOccurencePeriod = (occurrence: {
  startAt: Date;
  endAt: Date | null;
  allday: boolean;
}): {
  startAt: Date;
  endAt: Date;
} => {
  let startAt = occurrence.startAt;
  let endAt = occurrence.endAt;

  // Set allday valid hours
  if (occurrence.allday) {
    const periodAllday = workScheduleBetweenItems.find(
      (w) => w.id === 'allday',
    )!;

    startAt = setHours(new Date(occurrence.startAt), periodAllday.value.start);
    endAt = setHours(new Date(occurrence.startAt), periodAllday.value.end);
  }

  return {
    startAt,
    endAt: endAt!,
  };
};

const bookAgendaSlot = (payload: BookAgendaSlotPayload) => {
  let occurencePeriod: {
    startAt: Date;
    endAt: Date;
  } | null = null;

  if (payload.occurrence) {
    occurencePeriod = transformApiOccurencePeriod(payload.occurrence);
  }

  return http.agendaSlots
    .bookById(payload.id, {
      occurrence: payload.occurrence
        ? { ...payload.occurrence, ...occurencePeriod! }
        : undefined,
    })
    .then<BookAgendaSlotResponse>(({ response, transform }) => {
      const result = transform.handle({
        response,
        attributes: {},
        relations: {
          reservations: ({
            entity,
          }: {
            entity: AgendaSlotReservationApi;
          }): BookAgendaSlotResponse['reservations'] => {
            const doctorSubstitute =
              transform.getApiEntityRelation<SlotReservationDoctorSubstitute>({
                relation: 'doctorSubstitute',
                relationships: entity.relationships,
                included: response.included,
              });

            return [
              {
                id: entity.id,
                status: entity.attributes.status,
                doctorSubstitute: {
                  id: doctorSubstitute!.id,
                  firstname: doctorSubstitute!.attributes.firstname,
                  lastname: doctorSubstitute!.attributes.lastname,
                  picture: doctorSubstitute!.attributes.picture ?? null,
                  installable: doctorSubstitute!.attributes.installable,
                  hasPhysicalSiteAccess: false,
                },
              },
            ];
          },
        },
        schema: bookAgendaSlotResponseSchema,
      });

      return result as unknown as BookAgendaSlotResponse;
    });
};

const useBookAgendaSlotMutation = (): UseMutationResult<
  BookAgendaSlotResponse,
  HttpErrors,
  BookAgendaSlotPayload
> => {
  const { t } = useTranslation();
  const slotBookingConflitErrorHandler = useSlotBookingConflitErrorHandler();

  return useMutation({
    mutationFn: bookAgendaSlot,
    onSuccess() {
      toast.success(t('http.book-agenda-slot.success'));
    },
    onError(error) {
      const payload = getErrorPayload({
        error,
        type: 'agenda_slot_booking_conflict',
      });
      if (payload) {
        slotBookingConflitErrorHandler.handle(payload);
        return;
      }
    },
  });
};

export default useBookAgendaSlotMutation;
